import { getCookie } from 'cookies-next'

type FbqFn = (...args: unknown[]) => void

declare global {
  interface Window {
    fbq?: FbqFn
  }
}

const trackMetaPixelEvent = (
  name: string,
  params?: Record<string, unknown>,
  options?: { custom?: boolean; eventID?: string },
) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return
  const method = options?.custom ? 'trackCustom' : 'track'
  if (options?.eventID) window.fbq(method, name, params, { eventID: options.eventID })
  else window.fbq(method, name, params)
}

// Fires when a user views the prize draw listing page or an individual draw's detail page.
export const trackPrizeDrawView = (params: {
  contentIds?: (string | number)[]
  contentName?: string
}) =>
  trackMetaPixelEvent('ViewContent', {
    content_type: 'prize_draw',
    content_ids: params.contentIds?.map(String),
    content_name: params.contentName,
  })

export interface MetaTrackingParams {
  fbp?: string
  fbc?: string
  event_source_url?: string
}

/**
 * Sent when a deposit starts, so game_service can send its conversion event to Meta (the Conversions
 * API) with the same browser match keys, even when the user never comes back from the payment page.
 */
export const getMetaTrackingParams = (): MetaTrackingParams | undefined => {
  if (typeof window === 'undefined') return undefined
  const fbp = getCookie('_fbp')
  const fbc = getCookie('_fbc')
  return {
    ...(typeof fbp === 'string' && fbp && { fbp }),
    ...(typeof fbc === 'string' && fbc && { fbc }),
    // Without the query string, which can carry payment references.
    event_source_url: `${window.location.origin}${window.location.pathname}`,
  }
}

export type DepositPaymentMethod = 'YASPA' | 'CASHFLOWS'

export interface SettledDeposit {
  transactionId: number
  amountPence: number
  // Which of the user's deposits this is (1 = first), decided by game_service when it settled.
  depositNumber: number
  paymentMethod?: DepositPaymentMethod
}

// Deposits already reported from this tab, so a reload of the return page doesn't report one again.
const TRACKED_DEPOSITS_KEY = 'metaTrackedDeposits'

const getTrackedDeposits = (): number[] => {
  try {
    const tracked = JSON.parse(sessionStorage.getItem(TRACKED_DEPOSITS_KEY) || '[]')
    return Array.isArray(tracked) ? tracked : []
  } catch {
    return []
  }
}

const rememberTrackedDeposit = (transactionId: number) => {
  try {
    sessionStorage.setItem(
      TRACKED_DEPOSITS_KEY,
      JSON.stringify([...getTrackedDeposits(), transactionId]),
    )
  } catch {
    // Storage unavailable: a repeat is still harmless, Meta deduplicates on the event id.
  }
}

/**
 * Fires once a wallet deposit has settled. Only a user's first deposit is a standard Purchase, so
 * Meta's ad bidding optimises for new paying users; later deposits are the custom Deposit event.
 * Spending the wallet balance fires nothing: that money was counted when it was deposited.
 * The eventID is the wallet transaction id, the same as game_service's Conversions API event, so
 * Meta counts the two as one.
 */
export const trackDepositCompleted = ({
  transactionId,
  amountPence,
  depositNumber,
  paymentMethod,
}: SettledDeposit) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return
  if (getTrackedDeposits().includes(transactionId)) return
  rememberTrackedDeposit(transactionId)

  const value = amountPence / 100
  const eventID = String(transactionId)
  const method = paymentMethod && { payment_method: paymentMethod }

  if (depositNumber === 1) {
    trackMetaPixelEvent(
      'Purchase',
      {
        value,
        currency: 'GBP',
        content_name: 'First Time Deposit',
        content_type: 'wallet_topup',
        is_first_time: true,
        num_items: 1,
        ...method,
      },
      { eventID },
    )
    return
  }

  trackMetaPixelEvent(
    'Deposit',
    {
      value,
      currency: 'GBP',
      deposit_number: depositNumber,
      is_first_time: false,
      ...method,
    },
    { custom: true, eventID },
  )
}

const PAYMENT_METHODS: Record<string, DepositPaymentMethod> = {
  yaspa_deposit: 'YASPA',
  cashflows_payment: 'CASHFLOWS',
  YASPA: 'YASPA',
  CARD: 'CASHFLOWS',
}

// Wallet transaction reference_type, or a checkout order's payment_method.
export const depositPaymentMethod = (source: string | null | undefined) =>
  source ? PAYMENT_METHODS[source] : undefined
